import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  computeVariance,
  formatIqd,
  fromMillidinars,
  type BoxBalanceSummaryDto,
  type DRAWER_REFUSALS,
  type Millidinars,
  type ResetBoxBalanceInput,
} from '@gc/shared';

import { ApiError } from '@/lib/api';
import { BoxSplitFigures } from './BoxSplitFigures';
import { resetBox, type BoxResetAuditReceiptDto } from './useBoxBalance';

interface ResetBoxDialogProps {
  box: BoxBalanceSummaryDto;
  currencySymbol: string;
  onClose: () => void;
  onReset: (receipt: BoxResetAuditReceiptDto) => void;
  /**
   * The box changed server-side (`box_balance_changed`): the parent must
   * re-fetch so this dialog re-renders the NEW split for a recount. Without
   * it the employee would be re-reading the very figures the server refused.
   */
  onBoxChanged: () => void;
  /**
   * The OWNER resetting someone else's box (owner, 2026-09-23). The dialog
   * then names whose box it is and posts to that box's own route; the server
   * still refuses anyone but the owner, so this only changes the words and
   * the address, never who is allowed.
   */
  forOther?: boolean;
}

/**
 * The "Cash counted" field is typed in whole IQD — the unit on the notes in
 * the drawer — and converted to millidinars here, with BigInt arithmetic and
 * never a float. Returns null when nothing (or anything non-numeric) has been
 * entered yet; "0" is a real count of an empty drawer, not the absence of one.
 */
export function countedIqdToMd(raw: string): bigint | null {
  if (!/^\d+$/.test(raw)) return null;
  return BigInt(raw) * 1000n;
}

/**
 * A nonzero variance demands one sentence of why (`variance_note_required`).
 * Null (no count entered yet) requires nothing — the submit button is what
 * blocks an uncounted submission.
 */
export function noteRequiredFor(varianceMd: bigint | null): boolean {
  return varianceMd != null && varianceMd !== 0n;
}

/**
 * The two refusals a reset meets since the cash drawer (2026-10-12), as the
 * key of the sentence to show. `box_retired`: the drawer is in use and boxes
 * are history. `portal_box_not_resettable`, whatever the drawer: the Portal
 * box is customers spending their balance on the PCs, and sweeping it would
 * put 11.2M of money that never existed as cash into the safe. Null for
 * every other code, which keeps its existing wording below.
 */
const RETIRED_BOX_REFUSALS = {
  box_retired: 'boxBalance.resetDialog.retired',
  portal_box_not_resettable: 'boxBalance.resetDialog.portalNotResettable',
} as const satisfies Partial<Record<(typeof DRAWER_REFUSALS)[number], string>>;

export function retiredBoxRefusalKey(code: string): string | null {
  return Object.hasOwn(RETIRED_BOX_REFUSALS, code)
    ? RETIRED_BOX_REFUSALS[code as keyof typeof RETIRED_BOX_REFUSALS]
    : null;
}

/**
 * The exact wire payload a submitted count produces — exported for tests.
 *
 * BOTH displayed figures ride along, not just the total: the server judges
 * variance against CASH, and an offsetting cash-out/account-in pair moves the
 * cash while leaving the total untouched. Sending only the total let that
 * slip past the stale-guard and archive a variance nobody's drawer had;
 * `expectedCashMd` is what makes the server refuse instead.
 */
export function buildResetPayload(
  split: { totalMd: string; cashMd: string },
  countedMd: bigint,
  note: string,
): ResetBoxBalanceInput {
  return {
    expectedTotalMd: split.totalMd,
    expectedCashMd: split.cashMd,
    countedCashMd: countedMd.toString(),
    ...(note.trim() ? { note: note.trim() } : {}),
  };
}

/**
 * Live variance readout, sign-aware in BOTH directions.
 *
 * The wording is "more/less than the expected cash", never "over/short":
 * expected cash can legitimately be NEGATIVE (a refund-heavy shift where the
 * drawer owes money), and in that state counting more than expected is the
 * suspicious direction. Hardcoding "short" as the bad case would accuse the
 * wrong employee, so both directions get the same neutral emphasis.
 */
export function VarianceLine({
  countedMd,
  expectedCashMd,
  currencySymbol,
}: {
  countedMd: bigint;
  expectedCashMd: bigint;
  currencySymbol: string;
}) {
  const { t } = useTranslation();
  const variance = computeVariance(countedMd, expectedCashMd);
  const money = (value: bigint) => formatIqd(fromMillidinars(value.toString()), { currencySymbol });

  if (variance === 0n) {
    return (
      <p data-variance="0" className="mt-2 text-sm text-content-secondary">
        {t('boxBalance.resetDialog.varianceZero')}
      </p>
    );
  }

  return (
    <p data-variance={variance.toString()} className="mt-2 text-sm font-medium text-ending">
      {t(
        variance > 0n
          ? 'boxBalance.resetDialog.varianceOver'
          : 'boxBalance.resetDialog.varianceUnder',
        { amount: money(variance < 0n ? -variance : variance) },
      )}
    </p>
  );
}

/**
 * "Reset box balance" confirmation — SPEC §12.1 + WAVE2-DESIGN §4.
 *
 * The employee resets their own box with no approval step, so this dialog is
 * where the three things that make the flow safe have to happen:
 *
 *  1. The split is shown again, in full. The employee is about to move a figure
 *     that is deliberately larger than the notes in the drawer; confirming a
 *     blended total would be confirming a number they cannot check.
 *  2. The total displayed is sent back with the request. If a payment lands
 *     between this screen rendering and the button being pressed, the server
 *     refuses rather than sweeping an amount nobody saw.
 *  3. The drawer is COUNTED, not acknowledged. The field starts empty on
 *     purpose — prefilling the expected figure invites rubber-stamping it, and
 *     a rubber-stamped count is worth less than no count at all.
 */
export function ResetBoxDialog({
  box,
  currencySymbol,
  onClose,
  onReset,
  onBoxChanged,
  forOther = false,
}: ResetBoxDialogProps) {
  const { t } = useTranslation();

  const [countedRaw, setCountedRaw] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const expectedCash = fromMillidinars(box.split.cashMd);
  const total = fromMillidinars(box.split.totalMd);
  const money = (value: Millidinars) => formatIqd(value, { currencySymbol });

  const title = forOther
    ? t('boxBalance.resetDialog.titleOther', { name: box.employeeName })
    : t('boxBalance.resetDialog.title');

  const countedMd = countedIqdToMd(countedRaw);
  const variance = countedMd == null ? null : computeVariance(countedMd, expectedCash);
  const noteRequired = noteRequiredFor(variance);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (countedMd == null) return;
    setError(null);
    setSubmitting(true);

    try {
      // The figures the employee was actually shown — total AND cash — so the
      // server can refuse to move (or judge a variance against) anything else.
      const receipt = await resetBox(
        buildResetPayload(box.split, countedMd, note),
        forOther ? box.employeeId : undefined,
      );
      onReset(receipt);
    } catch (caught) {
      const retiredKey = caught instanceof ApiError ? retiredBoxRefusalKey(caught.code) : null;
      if (retiredKey) {
        setError(t(retiredKey));
      } else if (caught instanceof ApiError && caught.code === 'box_balance_changed') {
        const actual = (caught.details as { actualMd?: string } | undefined)?.actualMd;
        setError(
          t('boxBalance.resetDialog.changed', {
            total: actual ? money(fromMillidinars(actual)) : money(total),
          }),
        );
        // The count was made against figures that no longer exist. Clearing it
        // forces a fresh count against the split the parent is re-fetching —
        // leaving the stale number in place would be prefilling by accident.
        setCountedRaw('');
        onBoxChanged();
      } else if (caught instanceof ApiError && caught.code === 'variance_note_required') {
        // Normally prevented client-side; the server remains the authority.
        setError(t('boxBalance.resetDialog.varianceNoteRequired'));
      } else if (caught instanceof ApiError && caught.code === 'box_already_empty') {
        setError(
          t(forOther ? 'boxBalance.resetDialog.emptyOther' : 'boxBalance.resetDialog.empty'),
        );
      } else if (caught instanceof ApiError && caught.code === 'owner_only') {
        setError(t('boxBalance.resetDialog.ownerOnly'));
      } else {
        setError(caught instanceof ApiError ? caught.message : t('boxBalance.resetDialog.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-full w-full max-w-lg overflow-auto rounded-card border border-subtle bg-ground p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">{title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-content-secondary">
          {forOther
            ? t('boxBalance.resetDialog.introOther', { name: box.employeeName })
            : t('boxBalance.resetDialog.intro')}
        </p>

        <div className="mt-4">
          <BoxSplitFigures split={box.split} currencySymbol={currencySymbol} dense />
        </div>

        <dl className="tabular mt-4 rounded-card bg-raised p-3 text-sm">
          <div className="flex justify-between py-0.5">
            <dt className="text-content-secondary">{t('boxBalance.resetDialog.moving')}</dt>
            <dd className="font-semibold text-content">{money(total)}</dd>
          </div>
          <div className="flex justify-between py-0.5">
            <dt className="text-content-secondary">{t('boxBalance.entryCount')}</dt>
            <dd className="text-content-secondary">{box.split.entryCount}</dd>
          </div>
        </dl>

        {/* The count replaces the old confirm-checkbox: an acknowledgement can
            be given without opening the drawer, a number cannot. */}
        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('boxBalance.resetDialog.countedLabel')}
          </span>
          <div className="flex items-center gap-2">
            <input
              value={countedRaw}
              onChange={(event) => setCountedRaw(event.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              autoComplete="off"
              placeholder={t('boxBalance.resetDialog.countedPlaceholder')}
              className="tabular w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary"
            />
            <span className="text-sm text-content-muted">{currencySymbol}</span>
          </div>
          <span className="mt-1 block text-xs text-content-muted">
            {t('boxBalance.resetDialog.countedHint')}
          </span>
        </label>

        {countedMd != null ? (
          <VarianceLine
            countedMd={countedMd}
            expectedCashMd={expectedCash}
            currencySymbol={currencySymbol}
          />
        ) : null}

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t(
              noteRequired ? 'boxBalance.resetDialog.noteRequired' : 'boxBalance.resetDialog.note',
            )}
          </span>
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={300}
            required={noteRequired}
            placeholder={t('boxBalance.resetDialog.notePlaceholder')}
            className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary"
          />
        </label>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('boxBalance.resetDialog.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || countedMd == null || (noteRequired && !note.trim())}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {submitting
              ? t('common.loading')
              : forOther
                ? t('boxBalance.resetDialog.submitOther', { name: box.employeeName })
                : t('boxBalance.resetDialog.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
